import { useState } from "react";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/modules/shared/ui/components/card";
import { Button } from "@/modules/shared/ui/components/button";
import { Badge } from "@/modules/shared/ui/components/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/modules/shared/ui/components/table";
import { Input } from "@/modules/shared/ui/components/input";
import { useAiUsageSummary, useAiUsageEvents } from "../../hooks/use-ai-usage";
import type { AiFeature } from "../../dtos/ai-usage.dto";

const featureLabels: Record<AiFeature, string> = {
  GenerateVacancy: "Generate Vacancy",
  ExtractCv: "CV Extraction",
  MatchScore: "Match Score",
  GenerateInterviewQuestions: "Interview Questions",
  ExtractCompanyInfo: "Company Info",
  GenerateCompanyDescription: "Company Description",
};

export function AiUsageDashboard() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 15;

  const fromParam = from || undefined;
  const toParam = to || undefined;

  const { data: summary, isLoading: summaryLoading } = useAiUsageSummary(fromParam, toParam);
  const { data: events, isLoading: eventsLoading } = useAiUsageEvents(page, pageSize, fromParam, toParam);

  return (
    <div className="space-y-6">
      {/* Date Filters */}
      <div className="flex flex-wrap gap-4 items-end">
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">From</label>
          <Input
            type="date"
            value={from}
            onChange={(e) => { setFrom(e.target.value); setPage(1); }}
            className="mt-1 w-40"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">To</label>
          <Input
            type="date"
            value={to}
            onChange={(e) => { setTo(e.target.value); setPage(1); }}
            className="mt-1 w-40"
          />
        </div>
        {(from || to) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setFrom(""); setTo(""); setPage(1); }}
          >
            Clear
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      {summaryLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : summary ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="rounded-xl">
              <CardContent className="pt-4">
                <p className="text-xs font-medium text-muted-foreground uppercase">Total Events</p>
                <p className="text-2xl font-bold text-foreground">{summary.totalEvents.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card className="rounded-xl">
              <CardContent className="pt-4">
                <p className="text-xs font-medium text-muted-foreground uppercase">Total Tokens Used</p>
                <p className="text-2xl font-bold text-foreground">{(summary.totalInputTokens + summary.totalOutputTokens).toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card className="rounded-xl">
              <CardContent className="pt-4">
                <p className="text-xs font-medium text-muted-foreground uppercase">Total Cost (ZAR)</p>
                <p className="text-2xl font-bold text-foreground">R{summary.totalLocalCurrencyCost.toFixed(2)}</p>
              </CardContent>
            </Card>
          </div>

          {/* By Feature Breakdown */}
          {summary.byFeature.length > 0 && (
            <Card className="rounded-2xl border border-border shadow-lg bg-card">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-foreground">Usage by Feature</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-border">
                      <TableHead className="bg-linear-to-br from-muted to-muted/50 px-6 py-4 text-xs font-semibold text-foreground uppercase tracking-wider">Feature</TableHead>
                      <TableHead className="bg-linear-to-br from-muted to-muted/50 px-6 py-4 text-xs font-semibold text-foreground uppercase tracking-wider text-center">Events</TableHead>
                      <TableHead className="bg-linear-to-br from-muted to-muted/50 px-6 py-4 text-xs font-semibold text-foreground uppercase tracking-wider text-center">Total Tokens Used</TableHead>
                      <TableHead className="bg-linear-to-br from-muted to-muted/50 px-6 py-4 text-xs font-semibold text-foreground uppercase tracking-wider text-right">Cost (ZAR)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {summary.byFeature.map((f) => (
                      <TableRow key={f.feature} className="border-b border-border hover:bg-muted/50 transition-colors duration-150">
                        <TableCell className="px-6 py-4 text-sm text-foreground font-medium">
                          {featureLabels[f.feature] ?? f.feature}
                        </TableCell>
                        <TableCell className="px-6 py-4 text-sm text-foreground text-center">{f.eventCount}</TableCell>
                        <TableCell className="px-6 py-4 text-sm text-foreground text-center">{(f.totalInputTokens + f.totalOutputTokens).toLocaleString()}</TableCell>
                        <TableCell className="px-6 py-4 text-sm text-foreground text-right font-semibold">R{f.totalLocalCurrencyCost.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      ) : null}

      {/* Events Table */}
      <Card className="rounded-2xl border border-border shadow-lg bg-card">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-foreground">AI Usage Events</CardTitle>
        </CardHeader>
        <CardContent>
          {eventsLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : !events || events.items.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No AI usage events found</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-border">
                    <TableHead className="bg-linear-to-br from-muted to-muted/50 px-6 py-4 text-xs font-semibold text-foreground uppercase tracking-wider">Date</TableHead>
                    <TableHead className="bg-linear-to-br from-muted to-muted/50 px-6 py-4 text-xs font-semibold text-foreground uppercase tracking-wider text-center">Feature</TableHead>
                    <TableHead className="bg-linear-to-br from-muted to-muted/50 px-6 py-4 text-xs font-semibold text-foreground uppercase tracking-wider text-center">Total Tokens</TableHead>
                    <TableHead className="bg-linear-to-br from-muted to-muted/50 px-6 py-4 text-xs font-semibold text-foreground uppercase tracking-wider text-center">Cost (ZAR)</TableHead>
                    <TableHead className="bg-linear-to-br from-muted to-muted/50 px-6 py-4 text-xs font-semibold text-foreground uppercase tracking-wider text-center">Duration</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.items.map((e) => (
                    <TableRow key={e.id} className="border-b border-border hover:bg-muted/50 transition-colors duration-150">
                      <TableCell className="px-6 py-4 text-sm text-foreground">
                        {new Date(e.createdAt).toLocaleDateString("en-ZA")}
                      </TableCell>
                      <TableCell className="px-6 py-4 text-sm text-foreground text-center">
                        <Badge variant="secondary">
                          {featureLabels[e.feature] ?? e.feature}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-6 py-4 text-sm text-foreground text-center">
                        {e.inputTokens.toLocaleString()} / {e.outputTokens.toLocaleString()}
                      </TableCell>
                      <TableCell className="px-6 py-4 text-sm text-foreground text-center font-semibold">
                        R{e.localCurrencyCost.toFixed(4)}
                      </TableCell>
                      <TableCell className="px-6 py-4 text-sm text-foreground text-center">
                        {e.durationMs > 0 ? `${(e.durationMs / 1000).toFixed(1)}s` : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {Math.ceil(events.totalCount / pageSize) > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {page} of {Math.ceil(events.totalCount / pageSize)} ({events.totalCount} total)
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" disabled={page >= Math.ceil(events.totalCount / pageSize)} onClick={() => setPage((p) => p + 1)}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
